import { TableHead } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react'
import BigLoader from '../common/BigLoader';
import { getTranslation } from '../models/Translation';
import AlternatingTableRow from '../table/AlternatingTableRow';
import ScrollableTable from '../table/ScrollableTable';
import StandardTableCell from '../table/StandardTableCell';
import TableHeadCell, { QueryOrder } from '../table/TableHeadCell';
import { MenuItemClick, MenuItemClicks } from '../models/Statistics';

type Period = 'last7days' | 'last30days';

type Props = {
    period: Period;
    externalData: MenuItemClicks | null;
    loading: boolean;
}

const ClickCountTable = ({ period, externalData, loading }: Props) => {
    const [orderBy, setOrderBy] = useState<QueryOrder | null>({ orderBy: "count", orderDir: "desc" });

    const displayedStatistics: MenuItemClick[] = React.useMemo(() => {
        if (externalData == null) return [];
        const usedItems = [...(externalData[period] ?? [])];
        if (orderBy?.orderBy === "name") {
            return usedItems.sort(compareItemsByName);
        }
        return usedItems.sort(compareItemsByClick);
    }, [externalData, orderBy, period]);

    function compareItemsByName(a: MenuItemClick, b: MenuItemClick) {
        const titleA = getTranslation(a.item?.translations ?? []).title.toLowerCase();
        const titleB = getTranslation(b.item?.translations ?? []).title.toLowerCase();
        if (orderBy?.orderDir === 'asc') return titleA.localeCompare(titleB);
        return titleB.localeCompare(titleA);
    }

    function compareItemsByClick(a: MenuItemClick, b: MenuItemClick) {
        if (orderBy?.orderDir === 'asc') return a.count - b.count;
        return b.count - a.count;
    }

    return (
        <>
            <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                {t("statistics.menu_item_clicks")}
            </div>
            <ScrollableTable>
                <TableHead>
                    <tr className="h-[56px] bg-neutral-deep">
                        <TableHeadCell className="w-3/4" orderKey="name" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                            {t("statistics.menu_item")}
                        </TableHeadCell>
                        <TableHeadCell align="right" orderKey="count" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                            {t("statistics.click_count")}
                        </TableHeadCell>
                    </tr>
                </TableHead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={2}>
                                <BigLoader />
                            </td>
                        </tr>
                    ) : (displayedStatistics.map((row, index) => {
                        const translation = getTranslation(row.item?.translations ?? []);
                        return (
                            <AlternatingTableRow key={`menu-item-${row.item_id}`} index={index}>
                                <StandardTableCell>{translation.title || `#${row.item_id}`}</StandardTableCell>
                                <StandardTableCell align="right">{row.count}</StandardTableCell>
                            </AlternatingTableRow>
                        );
                    }))}
                </tbody>
            </ScrollableTable>
        </>
    )
}

export default ClickCountTable
